import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, DeltaPill, FilterChips, Drawer, DrawerHeader, Empty, ErrorState, FieldLabel, Loading, Pill, StatCard, inputClass } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api } from "@/lib/api";
import { eur, num, relative } from "@/lib/format";
import { usePlatformAdmins, usePlatformOverview, usePlatformUsers } from "@/lib/queries";
import { dateFR } from "@/lib/format";
import { switchOrganization } from "@/lib/organization";

/**
 * Niveau plateforme : tous les réseaux d'un coup d'œil (CA de la période,
 * tendance, alertes), création de réseaux et invitation de leurs
 * propriétaires, administrateurs plateforme.
 */
export function PlatformPage() {
  const { me } = useSession();
  const q = usePlatformOverview();
  const [creating, setCreating] = useState(false);
  const [inviteFor, setInviteFor] = useState<{ id: string; name: string } | null>(null);
  if (!me.platformAdmin) return <Navigate to="/" replace />;
  const d = q.data;

  return (
    <div className="flex flex-col gap-[18px]">
      <AccountApprovals />
      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {d && (
        <>
          <div className="flex flex-wrap gap-4">
            <StatCard
              size="xl"
              className="flex-[1.5_1_260px]"
              label={`CA tous réseaux — ${d.period.title}`}
              value={eur(d.totals.ca)}
            >
              <div className="mt-2 flex items-center gap-2">
                <DeltaPill delta={d.totals.delta} />
                <span className="text-[12px] text-[oklch(0.65_0.01_150)]">{d.period.cmpLabel}</span>
              </div>
            </StatCard>
            <StatCard className="flex-[1_1_140px]" label="Réseaux" value={num(d.totals.networks)} />
            <StatCard className="flex-[1_1_140px]" label="Boutiques" value={num(d.totals.boutiques)} />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="text-[15px] font-bold">Réseaux</div>
            <div className="flex-1" />
            <Button onClick={() => setCreating(true)}>+ Nouveau réseau</Button>
          </div>

          {d.networks.length === 0 ? (
            <Empty>Aucun réseau.</Empty>
          ) : (
            <div className="grid gap-[14px] [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
              {d.networks.map((n) => (
                <Card key={n.id} pad="px-5 py-[18px]" className="flex flex-col">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-bold">{n.name}</div>
                      <div className="mt-[2px] text-[11.5px] text-muted-2">
                        {n.boutiques} boutique{n.boutiques > 1 ? "s" : ""} · {n.members} membre{n.members > 1 ? "s" : ""}
                      </div>
                    </div>
                    {n.id === me.organization?.id && <Pill className="bg-accent-tint text-accent">Courant</Pill>}
                  </div>
                  <div className="mt-3 font-display text-[24px] font-extrabold tabular">{eur(n.ca)}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <DeltaPill delta={n.delta} className="text-[11.5px]" />
                    {n.lateTasks > 0 && <Pill className="bg-danger-bg text-danger">{n.lateTasks} tâche(s) en retard</Pill>}
                  </div>
                  <div className="mt-3 text-[11.5px] text-muted-2">
                    {n.owners.length ? `Propriétaire : ${n.owners.join(", ")}` : "Aucun propriétaire — à inviter"}
                    <br />
                    {n.lastSyncAt ? `Dernière synchro ${relative(n.lastSyncAt)}` : "Jamais synchronisé"}
                  </div>
                  <div className="flex-1" />
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={() => void switchOrganization(n.id)} className="px-4 py-2 text-[12px]">
                      Ouvrir le réseau
                    </Button>
                    <Button variant="outline" onClick={() => setInviteFor({ id: n.id, name: n.name })} className="px-4 py-2 text-[12px]">
                      Inviter un propriétaire
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <PlatformAdmins />
      <CreateNetworkDrawer open={creating} onClose={() => setCreating(false)} />
      <InviteDrawer target={inviteFor} onClose={() => setInviteFor(null)} />
    </div>
  );
}

function LinkBox({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-card-sm bg-accent-soft px-4 py-3">
      <div className="text-[12px] font-bold text-accent">Lien d'invitation à transmettre</div>
      <div className="mt-1 break-all text-[12px]">{link}</div>
      <button
        type="button"
        className="mt-2 text-[12px] font-bold text-accent"
        onClick={() => void navigator.clipboard.writeText(link).then(() => setCopied(true))}
      >
        {copied ? "Copié ✓" : "Copier le lien"}
      </button>
    </div>
  );
}

function CreateNetworkDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setName("");
    setOwnerEmail("");
    setError(null);
    setLink(null);
    onClose();
  };
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ invitation: { link: string } | null }>("/platform/organizations", { name, ownerEmail });
      await Promise.all([qc.invalidateQueries({ queryKey: ["platform-overview"] }), qc.invalidateQueries({ queryKey: ["me"] })]);
      if (r.invitation) setLink(r.invitation.link);
      else close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Création impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer open={open} onClose={close}>
      <DrawerHeader title="Nouveau réseau" onClose={close} />
      {link ? (
        <>
          <LinkBox link={link} />
          <Button onClick={close}>Terminer</Button>
        </>
      ) : (
        <>
          <div>
            <FieldLabel>Nom du réseau</FieldLabel>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Green Corner" />
          </div>
          <div>
            <FieldLabel>E-mail du propriétaire (facultatif)</FieldLabel>
            <input className={inputClass} type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
            <div className="mt-1 text-[11.5px] text-muted">Il recevra un lien pour créer son compte et rejoindre le réseau en tant que propriétaire.</div>
          </div>
          {error && <ErrorState error={new Error(error)} />}
          <div className="flex-1" />
          <div className="flex gap-[10px]">
            <Button variant="outline" onClick={close} className="flex-1">
              Annuler
            </Button>
            <Button onClick={() => void submit()} disabled={busy || !name.trim()} className="flex-1">
              Créer
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}

function InviteDrawer({ target, onClose }: { target: { id: string; name: string } | null; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"OWNER" | "ADMIN">("OWNER");
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const close = () => {
    setEmail("");
    setError(null);
    setLink(null);
    onClose();
  };
  const submit = async () => {
    setError(null);
    try {
      const r = await api.post<{ link: string }>(`/platform/organizations/${target!.id}/invitations`, { email, role });
      setLink(r.link);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invitation impossible");
    }
  };
  return (
    <Drawer open={Boolean(target)} onClose={close}>
      <DrawerHeader title={`Inviter dans ${target?.name ?? ""}`} onClose={close} />
      {link ? (
        <>
          <LinkBox link={link} />
          <Button onClick={close}>Terminer</Button>
        </>
      ) : (
        <>
          <div>
            <FieldLabel>E-mail</FieldLabel>
            <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <FieldLabel>Rôle</FieldLabel>
            <select className={inputClass} value={role} onChange={(e) => setRole(e.target.value as "OWNER" | "ADMIN")}>
              <option value="OWNER">Propriétaire du réseau</option>
              <option value="ADMIN">Tête de réseau</option>
            </select>
          </div>
          {error && <ErrorState error={new Error(error)} />}
          <div className="flex-1" />
          <Button onClick={() => void submit()} disabled={!email.trim()}>
            Créer l'invitation
          </Button>
        </>
      )}
    </Drawer>
  );
}

function PlatformAdmins() {
  const { me } = useSession();
  const qc = useQueryClient();
  const q = usePlatformAdmins();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["platform-admins"] });
  const add = async () => {
    setError(null);
    try {
      await api.post("/platform/admins", { email });
      setEmail("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ajout impossible");
    }
  };
  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Retirer l'accès plateforme de ${name} ?`)) return;
    await api.del(`/platform/admins/${id}`);
    await refresh();
  };
  return (
    <Card>
      <div className="text-[15px] font-bold">Administrateurs plateforme</div>
      <div className="mt-1 text-[12.5px] text-muted">
        Voient et gèrent tous les réseaux, avec les droits propriétaire dans chacun.
      </div>
      <div className="mt-3 flex flex-col">
        {(q.data ?? []).map((a) => (
          <div key={a.id} className="flex items-center gap-3 border-t border-line-soft py-[10px] text-[13px]">
            <span className="font-semibold">{a.name}</span>
            <span className="text-muted-2">{a.email}</span>
            <div className="flex-1" />
            {a.id !== me.user.id && (
              <button type="button" className="text-[12px] font-bold text-danger" onClick={() => void remove(a.id, a.name)}>
                Retirer
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <input className={`${inputClass} max-w-[320px]`} type="email" placeholder="E-mail d'un compte existant" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button onClick={() => void add()} disabled={!email.trim()}>
          Ajouter
        </Button>
      </div>
      {error && <div className="mt-2 text-[12.5px] font-semibold text-danger">{error}</div>}
    </Card>
  );
}

const APPROVAL_TABS = [
  { key: "PENDING" as const, label: "En attente" },
  { key: "APPROVED" as const, label: "Validés" },
  { key: "REJECTED" as const, label: "Refusés" },
];

/**
 * Validation des comptes : une inscription libre n'a accès à rien tant qu'un
 * administrateur plateforme ne l'a pas validée. (Un compte qui accepte une
 * invitation est validé par celle-ci.)
 */
function AccountApprovals() {
  const { me } = useSession();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"PENDING" | "APPROVED" | "REJECTED">("PENDING");
  const q = usePlatformUsers(status);
  const [error, setError] = useState<string | null>(null);
  const act = async (id: string, action: "approve" | "reject", name: string) => {
    if (action === "reject" && !window.confirm(`Refuser l'accès de ${name} ? Ses sessions seront fermées.`)) return;
    setError(null);
    try {
      await api.post(`/platform/users/${id}/${action}`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["platform-users"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action impossible");
    }
  };
  const rows = q.data ?? [];
  if (status === "PENDING" && rows.length === 0 && me.pendingApprovals === 0 && !q.isLoading) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-card-sm bg-accent-soft px-5 py-3 text-[12.5px] text-[oklch(0.3_0.03_152)]">
        <span className="font-bold">Comptes à valider :</span> aucun en attente.
        <div className="flex-1" />
        <button type="button" className="font-semibold text-accent" onClick={() => setStatus("APPROVED")}>
          Voir les comptes validés
        </button>
      </div>
    );
  }
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[15px] font-bold">Comptes à valider</div>
        {me.pendingApprovals > 0 && <Pill className="bg-warn-bg text-warn">{me.pendingApprovals} en attente</Pill>}
        <div className="flex-1" />
        <FilterChips options={APPROVAL_TABS} value={status} onChange={setStatus} />
      </div>
      <div className="mt-1 text-[12.5px] text-muted">
        Personne n'accède à l'application sans validation. Un compte qui accepte une invitation est validé automatiquement.
      </div>
      {error && <ErrorState error={new Error(error)} className="mt-3" />}
      <div className="mt-3 overflow-x-auto">
        <div className="min-w-[640px]">
          {q.isLoading && <Loading />}
          {!q.isLoading && rows.length === 0 && <Empty>Aucun compte.</Empty>}
          {rows.map((u) => (
            <div key={u.id} className="grid grid-cols-[1.3fr_1.2fr_110px_auto] items-center gap-3 border-t border-line-soft py-3 text-[13px]">
              <div className="min-w-0">
                <div className="truncate font-semibold">{u.name}</div>
                <div className="truncate text-[11.5px] text-muted-2">{u.email}</div>
              </div>
              <div className="min-w-0 text-[11.5px] text-muted-2">
                {u.networks.length ? `Membre : ${u.networks.join(", ")}` : u.pendingInvitations.length ? `Invité·e : ${u.pendingInvitations.join(", ")}` : "Aucun réseau ni invitation"}
                <br />
                Connexion : {u.providers.map((p) => (p === "apple" ? "Apple" : "e-mail")).join(", ") || "—"}
              </div>
              <div className="text-[11.5px] text-muted-2">
                {status === "PENDING" ? `Inscrit le ${dateFR(u.createdAt)}` : u.approvedAt ? `Validé le ${dateFR(u.approvedAt)}` : ""}
              </div>
              <div className="flex justify-end gap-2">
                {status !== "APPROVED" && (
                  <Button onClick={() => void act(u.id, "approve", u.name)} className="px-4 py-2 text-[12px]">
                    Valider
                  </Button>
                )}
                {status !== "REJECTED" && u.id !== me.user.id && (
                  <Button variant="outline" onClick={() => void act(u.id, "reject", u.name)} className="px-4 py-2 text-[12px] text-danger">
                    {status === "APPROVED" ? "Révoquer" : "Refuser"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
